import React, { useMemo, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { RSS_SOURCES, RSS_CATEGORIES, getRssSourceByUrl } from '@/lib/rss-sources';

// Single shared picker used inside both the Listening Rule and Fence dialogs.
// State is the array of full RSS URLs (built-in + custom). Built-ins are
// shown as toggles grouped by category; custom URLs appear as removable chips.

interface RssSourcePickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export const RssSourcePicker: React.FC<RssSourcePickerProps> = ({ value, onChange }) => {
  const [customUrl, setCustomUrl] = useState('');
  const builtInUrls = useMemo(() => new Set(RSS_SOURCES.map(s => s.url)), []);
  const selected = useMemo(() => new Set(value), [value]);

  const customUrls = useMemo(
    () => value.filter(url => !builtInUrls.has(url)),
    [value, builtInUrls],
  );

  const toggle = (url: string, on: boolean) => {
    if (on) {
      if (selected.has(url)) return;
      onChange([...value, url]);
    } else {
      onChange(value.filter(u => u !== url));
    }
  };

  const addCustom = () => {
    const trimmed = customUrl.trim();
    if (!trimmed) return;
    if (!/^https?:\/\//i.test(trimmed)) {
      return;
    }
    if (value.includes(trimmed)) {
      setCustomUrl('');
      return;
    }
    onChange([...value, trimmed]);
    setCustomUrl('');
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>RSS publications</Label>
        <p className="text-xs text-muted-foreground">
          Selected feeds are fetched on the same schedule as your social platforms.
        </p>
      </div>

      <div className="space-y-3">
        {RSS_CATEGORIES.map(category => {
          const inCategory = RSS_SOURCES.filter(s => s.category === category);
          if (inCategory.length === 0) return null;
          return (
            <div key={category}>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                {category}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {inCategory.map(source => (
                  <label
                    key={source.id}
                    className="flex items-center gap-2 cursor-pointer text-sm py-1"
                  >
                    <Switch
                      checked={selected.has(source.url)}
                      onCheckedChange={(checked) => toggle(source.url, checked)}
                    />
                    <span>{source.name}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <Label htmlFor="rss-custom">Custom RSS URL</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="rss-custom"
            type="url"
            placeholder="https://example.com/feed.xml"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom();
              }
            }}
          />
          <Button type="button" variant="outline" onClick={addCustom} disabled={!customUrl.trim()}>
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>

        {customUrls.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {customUrls.map(url => (
              <Badge key={url} variant="outline" className="gap-1 pr-1">
                <span className="max-w-[16rem] truncate text-xs">{url}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0.5"
                  onClick={() => toggle(url, false)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export function isBuiltInRssSource(url: string): boolean {
  return Boolean(getRssSourceByUrl(url));
}
